import React, { useState, useEffect } from 'react'
import { XMarkIcon, CalendarIcon, ClockIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline'

interface ScheduledEvent {
  id: string
  time: string
  title: string
  description?: string
  location?: string
  eventUrl?: string
  coordinator?: string
  coordinatorName?: string
  isOptional?: boolean
  maxParticipants?: number | null
  deadline?: string | null
  rsvps?: Array<string | { userId: string; name: string; rsvpedAt: string }>
}

interface EditScheduledEventModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (eventData: any) => void
  scheduledEvent: ScheduledEvent
  date: string
}

const EditScheduledEventModal: React.FC<EditScheduledEventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  scheduledEvent,
  date
}) => {
  const [formData, setFormData] = useState({
    time: '',
    title: '',
    description: '',
    location: '',
    eventUrl: '',
    coordinatorName: '',
    isOptional: false,
    maxParticipants: '',
    deadline: ''
  })

  useEffect(() => {
    if (scheduledEvent) {
      setFormData({
        time: scheduledEvent.time || '',
        title: scheduledEvent.title || '',
        description: scheduledEvent.description || '',
        location: scheduledEvent.location || '',
        eventUrl: scheduledEvent.eventUrl || '',
        coordinatorName: scheduledEvent.coordinatorName || '',
        isOptional: scheduledEvent.isOptional || false,
        maxParticipants: scheduledEvent.maxParticipants ? scheduledEvent.maxParticipants.toString() : '',
        deadline: scheduledEvent.deadline ? new Date(scheduledEvent.deadline).toISOString().slice(0, 16) : ''
      })
    }
  }, [scheduledEvent])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.time.trim() || !formData.title.trim()) {
      alert('Time and title are required')
      return
    }

    const eventData = {
      time: formData.time.trim(),
      title: formData.title.trim(),
      description: formData.description.trim(),
      location: formData.location.trim(),
      eventUrl: formData.eventUrl.trim() || undefined,
      coordinatorName: formData.coordinatorName.trim(),
      isOptional: formData.isOptional,
      maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
      deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null
    }

    onSave(eventData)
    onClose()
  }

  const formatDateForDisplay = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit Activity
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-blue-700 dark:text-blue-300">
            <CalendarIcon className="w-4 h-4" />
            <span>{formatDateForDisplay(date)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Time *
            </label>
            <div className="relative">
              <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Activity title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Activity description"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Event Website URL (optional)
            </label>
            <input
              type="url"
              value={formData.eventUrl}
              onChange={(e) => setFormData({ ...formData, eventUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://example.com/activity-info"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Link to venue website, registration, or additional information
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="Location"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Coordinator
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={formData.coordinatorName}
                onChange={(e) => setFormData({ ...formData, coordinatorName: e.target.value })}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="Coordinator name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Participants
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="No limit"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                RSVP Deadline
              </label>
              <input
                type="datetime-local"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isOptional"
              checked={formData.isOptional}
              onChange={(e) => setFormData({ ...formData, isOptional: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
            />
            <label htmlFor="isOptional" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              This activity is optional
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditScheduledEventModal
